/**
 * kd-algolia-autocomplete: Predictive search with @algolia/autocomplete-js
 * Replaces initAutocomplete in algolia-verification.js
 */
import { LitElement, nothing } from 'lit';
import { customElement } from 'lit/decorators.js';
import { buildSystemFilters } from './utils/algolia-filters';
import { getAlgoliaConfig, createSearchClient, waitForAlgoliaLibs } from './utils/algolia-client';

interface AutocompleteLib {
  autocomplete: (config: Record<string, unknown>) => void;
  getAlgoliaResults: (config: Record<string, unknown>) => Promise<unknown[]>;
}

@customElement('kd-algolia-autocomplete')
export class KdAlgoliaAutocomplete extends LitElement {
  createRenderRoot() { return this; }

  async connectedCallback(): Promise<void> {
    super.connectedCallback();

    const config = getAlgoliaConfig();
    if (!config?.connected) return;

    try {
      await waitForAlgoliaLibs({ autocomplete: true });
      this._initAutocomplete(config);
    } catch {
      // Libraries failed to load
    }
  }

  private _initAutocomplete(config: NonNullable<ReturnType<typeof getAlgoliaConfig>>): void {
    const searchClient = createSearchClient({ cacheKey: 'algolia-responses-search' });
    if (!searchClient) return;

    const autocompleteJs = window['@algolia/autocomplete-js'] as AutocompleteLib | undefined;
    if (!autocompleteJs?.autocomplete) return;

    const container = this.querySelector('#algolia-autocomplete');
    if (!container) return;

    // Clear placeholder
    container.innerHTML = '';

    const systemFilters = buildSystemFilters(config.customer);

    autocompleteJs.autocomplete({
      container: '#algolia-autocomplete',
      placeholder: '商品を検索...',
      openOnFocus: false,
      getSources: (params: { query: string }) => {
        const query = params.query;
        if (!query) return [];
        return [
          {
            sourceId: 'products',
            getItems: () =>
              autocompleteJs.getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: config.indexName,
                    query,
                    params: {
                      hitsPerPage: 10,
                      distinct: true,
                      filters: systemFilters,
                    },
                  },
                ],
              }),
            templates: {
              header: () => '商品',
              item: (params: {
                item: { handle?: string; image?: string; price?: number; title?: string };
                html: (s: TemplateStringsArray, ...v: unknown[]) => unknown;
                components: { Highlight: (p: { hit: unknown; attribute: string }) => unknown };
              }) => {
                const { item, html: h, components } = params;
                const price = item.price ? `¥${Number(item.price).toLocaleString()}` : '';
                return h`<a href="/products/${item.handle || ''}" class="aa-ItemLink">
                  <div class="aa-ItemContent">
                    ${item.image ? h`<img src="${item.image}" alt="" width="40" height="40" class="object-cover rounded" />` : ''}
                    <div class="aa-ItemContentBody">
                      <div class="aa-ItemContentTitle">${components.Highlight({ hit: item, attribute: 'title' })}</div>
                      <div class="aa-ItemContentDescription">${price}</div>
                    </div>
                  </div>
                </a>`;
              },
              noResults: () => '検索結果がありません',
            },
          },
        ];
      },
    });
  }

  render() {
    return nothing;
  }
}
